import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface ProcessStep {
  id: number;
  title: string;
  description: string;
  icon: string;
  duration: string;
  details: string[];
}

// Individual Process Card Component with viewport-based animation
const ProcessCard: React.FC<{
  step: ProcessStep;
  index: number;
  isAnimated: boolean;
  isMobile: boolean;
}> = ({ step, index, isAnimated, isMobile }) => {
  const isLeft = index % 2 === 0;

  return (
    <div
      className={`relative flex flex-col md:flex-row items-center ${
        isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
      }`}
    >
      {/* Step Content */}
      <motion.div 
        className={`w-full md:w-5/12 ${isLeft ? 'md:pr-0 md:-mr-4' : 'md:pl-0 md:-ml-4'}`}
        initial={{ x: isMobile ? (isLeft ? -200 : 200) : (isLeft ? -200 : 200), opacity: 0 }}
        whileInView={{ x: 0, opacity: 1 }}
        viewport={{ once: false, margin: "-100px" }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="glass rounded-2xl px-4 py-4 hover:bg-opacity-30 transition-all duration-300 group">
          {/* Duration Badge and Step Number */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-bold text-accent-light">
              Step {step.id}
            </div>
            <div className="inline-block px-3 py-1 text-accent-light text-xs font-semibold rounded-full bg-accent/10">
              {step.duration}
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold dark:text-white light:text-gray-900 mb-2 group-hover:text-accent-light transition-colors duration-300">
            {step.title}
          </h3>

          {/* Description */}
          <p className="dark:text-gray-300 light:text-gray-600 mb-2 leading-relaxed text-sm">
            {step.description}
          </p>

          {/* Details */}
          <ul className="space-y-2">
            {step.details.map((detail, detailIndex) => (
              <li key={detailIndex} className="flex items-center text-xs dark:text-gray-400 light:text-gray-500">
                <span className="w-1.5 h-1.5 bg-accent rounded-full mr-3"></span>
                {detail}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>

      {/* Step Icon */}
      <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 z-10">
        <motion.div 
          className={`w-16 h-16 glass rounded-full flex items-center justify-center text-2xl transition-all duration-500 transform ${
            isAnimated
              ? 'scale-100 bg-accent/20 border-2 border-accent' 
              : 'scale-75 bg-gray-600/20 border-2 border-gray-600'
          }`}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: false, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <span className={isAnimated ? 'animate-bounce' : ''}>
            {step.icon}
          </span>
        </motion.div>
      </div>
      
      {/* Mobile Step Icon */}
      <div className="md:hidden flex justify-center mb-4">
        <div className={`w-12 h-12 glass rounded-full flex items-center justify-center text-xl transition-all duration-500 transform ${
          isAnimated
            ? 'scale-100 bg-accent/20 border-2 border-accent' 
            : 'scale-75 bg-gray-600/20 border-2 border-gray-600'
        }`}>
          <span className={isAnimated ? 'animate-bounce' : ''}>
            {step.icon}
          </span>
        </div>
      </div>

      {/* Spacer for alternating layout */}
      <div className="hidden md:block w-6/12"></div>
    </div>
  );
};

const Process: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedSteps, setAnimatedSteps] = useState<number[]>([]);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsMobile(('matches' in e ? e.matches : (e as MediaQueryList).matches));
    };

    // Set initial
    handleChange(mq);
    // Listen for changes
    const listener = (e: MediaQueryListEvent) => handleChange(e);
    mq.addEventListener ? mq.addEventListener('change', listener) : mq.addListener(listener);

    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', listener) : mq.removeListener(listener);
    };
  }, []);

  const steps: ProcessStep[] = [
    {
      id: 1,
      title: 'Discovery',
      description: 'Understanding your business needs and project requirements',
      icon: '🔍',
      duration: '1-2 weeks',
      details: ['Requirements gathering', 'Stakeholder interviews', 'Market research', 'Technical feasibility analysis']
    },
    {
      id: 2,
      title: 'Planning',
      description: 'Creating a comprehensive project roadmap and architecture',
      icon: '📋',
      duration: '1 week',
      details: ['Project roadmap', 'Technical architecture', 'Resource allocation', 'Timeline planning']
    },
    {
      id: 3,
      title: 'Design',
      description: 'Crafting intuitive user interfaces and user experiences',
      icon: '🎨',
      duration: '2-3 weeks',
      details: ['UI/UX design', 'Wireframing', 'Prototyping', 'Design system creation']
    },
    {
      id: 4,
      title: 'Development',
      description: 'Building robust and scalable software solutions',
      icon: '⚙️',
      duration: '4-12 weeks',
      details: ['Frontend development', 'Backend development', 'API integration', 'Database design']
    },
    {
      id: 5,
      title: 'Testing',
      description: 'Ensuring quality and performance through rigorous testing',
      icon: '🧪',
      duration: '1-2 weeks',
      details: ['Unit testing', 'Integration testing', 'Performance testing', 'Security testing']
    },
    {
      id: 6,
      title: 'Launch',
      description: 'Deploying your solution to production with monitoring',
      icon: '🚀',
      duration: '1 week',
      details: ['Production deployment', 'Performance monitoring', 'User training', 'Documentation']
    },
    {
      id: 7,
      title: 'Support',
      description: 'Ongoing maintenance and continuous improvement',
      icon: '🛠️',
      duration: 'Ongoing',
      details: ['Bug fixes', 'Feature updates', 'Performance optimization', '24/7 support']
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.01, rootMargin: '200px' } // Much earlier trigger
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || hasAnimated) return;

    // Clear any existing timeouts
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current = [];

    steps.forEach((step, index) => {
      const timeout = setTimeout(() => {
        setAnimatedSteps(prev => [...prev, step.id]);
      }, index * 5); // Much faster animation - 5ms between steps
      timeoutsRef.current.push(timeout);
    });

    setHasAnimated(true);

    // Cleanup function
    return () => {
      timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
      timeoutsRef.current = [];
    };
  }, [isVisible, hasAnimated, steps]);

  return (
    <section ref={sectionRef} id="process" className="py-10 dark:bg-navy-light light:bg-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8  overflow-hidden">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold dark:text-white light:text-gray-900 mb-6">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-light">Process</span>
          </h2>
          <p className="text-xl dark:text-gray-300 light:text-gray-600 max-w-3xl mx-auto">
            We follow a proven methodology that ensures your project is delivered on time, 
            within budget, and exceeds your expectations.
          </p>
        </div>

        {/* Professional Left-Right Flow Chart */}
        <div className="relative max-w-5xl mx-auto">
          {/* Vertical Flow Line - Hidden on mobile */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-accent via-accent-light to-accent opacity-30">
            <div 
              className="w-full bg-gradient-to-b from-accent to-accent-light transition-all duration-2000 ease-out"
              style={{ 
                height: isVisible ? '100%' : '0%',
                transitionDelay: '0.5s'
              }}
            ></div>
          </div>

          {/* Process Steps */}
          <div className="space-y-6 md:space-y-0">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <ProcessCard
                  step={step}
                  index={index}
                  isAnimated={animatedSteps.includes(step.id)}
                  isMobile={isMobile}
                />
                
                {/* Connecting Line to Next Step - Hidden on mobile */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 z-5" style={{ top: '100%', marginTop: '8px' }}>
                    <div className="w-0.5 h-4 bg-accent opacity-60"></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
